import type { FC } from 'react';
import { useEffect } from 'react';
import type { BeneficialOwnerResponse, IANATimezones } from '@gcv/shared';
import { BeneficialOwnerUpdateAction } from '@gcv/shared';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import StarIcon from '@mui/icons-material/Star';
import { Box, IconButton } from '@mui/material';

import { environment } from 'environments/environment';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { useDocuments } from 'tanstack-hooks';
import { Button, Divider, Drawer, FileContainer, Header, LabelValue, Spinner, Text, Tooltip } from 'ui';
import { getMostRecentCertification } from 'ui/apps/util/beneficial-owners';
import palette from 'ui/theme/palette';
import { DateTimeHelpers } from 'util/dateTime.util';
import { BeneficialOwnerDrawerUpdateBlurb } from './beneficial-owner-drawer-update-blurb';

interface Props {
  open: boolean;
  onClose: () => void;
  owner: BeneficialOwnerResponse;
  orgId: string;
  orgName: string;
  timezone: IANATimezones;
  dataCy: string;
  onEditOwner: () => void;
  onArchiveOwner: () => void;
  isArchivingOwner: boolean;
  controlProngTooltip: string;
  canEditOwner?: boolean;
  isAccountArchived?: boolean;
  userPermissionedToEditBeneficialOwners?: boolean;
  questionnaireRoute?: string;
}

export const BeneficialOwnerDrawer: FC<Props> = (props) => {
  const {
    open,
    onClose,
    owner,
    orgId,
    orgName,
    timezone,
    dataCy,
    onEditOwner,
    onArchiveOwner,
    isArchivingOwner,
    controlProngTooltip,
    canEditOwner = true,
    isAccountArchived = false,
    userPermissionedToEditBeneficialOwners = true,
    questionnaireRoute
  } = props;
  const documentIds = owner.identification_doument_ids ?? [];
  const { documentsMap, isErrorDocuments, isLoadingDocuments } = useDocuments(
    open && documentIds.length ? orgId : ''
  );
  const documents = documentIds.map((documentId) => documentsMap[documentId]).filter(Boolean);
  const cityStateZip = [owner.city, [owner.state, owner.zip_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const providerPermissionsTooltip =
    'This account has opted out of allowing service providers to edit their company and user information. ' +
    'Please contact the account directly or support@greencheckverified.com for assistance.';

  useEffect(() => {
    if (isErrorDocuments) {
      getSnackbarStore().showErrorSnackbarMessage('There was an issue getting attached documents.');
    }
  }, [isErrorDocuments]);

  const mostRecentCertification = getMostRecentCertification(owner);

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
            <Header
              dataCy="drawer-title"
              type="h2"
              content={`${[
                owner.beneficial_owner_first_name,
                owner.middle_initial,
                owner.beneficial_owner_last_name
              ]
                .filter(Boolean)
                .join(' ')}${owner.archived ? ' (Archived)' : ''}`}
            />
            {owner.control_prong === true && (
              <Tooltip title={controlProngTooltip} placement="top">
                <StarIcon
                  color={owner.archived ? 'disabled' : 'primary'}
                  sx={{ height: '20px', width: '20px' }}
                />
              </Tooltip>
            )}
          </Box>
        }
        subtitle={orgName}
        width="xlarge"
        bottomAction={
          <Box sx={{ display: 'flex', gap: 2, p: 2 }}>
            {!!userPermissionedToEditBeneficialOwners && !isAccountArchived && (
              <>
                <Tooltip
                  title={owner.archived ? 'Archived owners cannot be edited.' : providerPermissionsTooltip}
                  disabled={!owner.archived && canEditOwner}
                >
                  <Button
                    color="primary"
                    label="Edit Owner"
                    onClick={onEditOwner}
                    disabled={isLoadingDocuments || owner.archived || !canEditOwner}
                  />
                </Tooltip>
                <Tooltip title={providerPermissionsTooltip} disabled={canEditOwner}>
                  <Button
                    color="default-outlined"
                    label={owner.archived ? 'Unarchive' : 'Archive'}
                    onClick={onArchiveOwner}
                    isLoading={isArchivingOwner}
                    disabled={!canEditOwner}
                  />
                </Tooltip>
              </>
            )}
            <Button color="default-outlined" label="Close" onClick={onClose} />
          </Box>
        }
        dataCy={dataCy}
      >
        <Box sx={{ display: 'grid', gap: 2, p: 3 }}>
          {isAccountArchived && (
            <Box
              sx={{
                backgroundColor: palette.background.secondary,
                border: `1px solid ${palette.text.disabled}`,
                borderRadius: '6px',
                display: 'grid',
                gap: 1,
                p: 2
              }}
            >
              <Text
                content="This account is archived. Beneficial ownership records are snapshotted at time of archiving and no longer accept or receive updates."
                type="body3"
              />
            </Box>
          )}
          <BeneficialOwnerDrawerUpdateBlurb owner={owner} timezone={timezone} />
          <Header content="Owner Information" type="h3" />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            <LabelValue label="Ownership %" value={`${owner.ownership_in_this_business}%`} />
            <LabelValue label="Job Title / Occupation" value={owner.job_title_occupation || '--'} />
            <LabelValue
              label="Date of Birth"
              value={DateTimeHelpers.formatDateStringToFriendlyDateString(owner.date_of_birth, undefined)}
              noBottomMargin
            />
          </Box>
          <Box>
            <LabelValue
              label="Address"
              value={
                <Box sx={{ display: 'grid' }}>
                  <Text type="body1" content={owner.street_address || '--'} />
                  <Text type="body1" content={cityStateZip || '--'} />
                  <Text type="body1" content={owner.country || '--'} />
                </Box>
              }
              noBottomMargin
            />
          </Box>
          <Box sx={{ pb: 1, pt: 1 }}>
            <Divider />
          </Box>
          <Header content="ID Documentation" type="h3" />
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: owner.us_person ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))'
            }}
          >
            <LabelValue
              label="US / Non-US Person"
              value={owner.us_person ? 'US Person' : 'Non-US Person'}
              noBottomMargin
            />
            <LabelValue
              label={owner.us_person ? 'SSN' : 'SSN, ITIN, Passport, etc.'}
              value={owner.identification_id || '--'}
              mask
              noBottomMargin
            />
            {owner.us_person === false && (
              <LabelValue
                label="Country of Issuance"
                value={owner.country_of_issuance || '--'}
                noBottomMargin
              />
            )}
          </Box>

          {isLoadingDocuments ? (
            <Spinner />
          ) : (
            <Box sx={{ width: '100%' }}>
              <FileContainer
                fullWidth
                multiple
                acceptedTypes="imagesAndPdfs"
                existingS3Files={documents || []}
                fileBucket={environment.storageConfig.orgDocument}
                viewOnly
                readOnly
                dataCy="beneficial-owner-documentation"
              />
            </Box>
          )}
          <Box sx={{ pb: 1, pt: 1 }}>
            <Divider />
          </Box>
          <Header content="Certification" type="h3" />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <LabelValue
              label="Last Certified"
              value={DateTimeHelpers.formatISOToFriendlyDateString(
                owner.last_update.update_action === BeneficialOwnerUpdateAction.Certify
                  ? owner.last_update.date
                  : mostRecentCertification?.date,
                timezone
              )}
              noBottomMargin
            />
            <LabelValue
              label="Certified Via"
              value={
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
                  <Text
                    content={
                      owner.last_update.update_action === BeneficialOwnerUpdateAction.Certify
                        ? owner.last_update.template_result_name ?? '--'
                        : mostRecentCertification?.template_result_name ?? '--'
                    }
                    type="body1"
                  />
                  {questionnaireRoute && (
                    <Tooltip title="View questionnaire">
                      <IconButton
                        aria-label="View questionnaire"
                        onClick={() => window.open(questionnaireRoute, '_blank')}
                        size="small"
                      >
                        <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              }
              noBottomMargin
            />
            <LabelValue
              label="Certified By"
              value={
                owner.last_update.update_action === BeneficialOwnerUpdateAction.Certify
                  ? owner.last_update.by_display_name ?? '--'
                  : mostRecentCertification?.by_display_name ?? '--'
              }
              noBottomMargin
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
